import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export default function FryLogForm({ editMode = false }) {
  const [title, setTitle] = useState('');
  const [rating, setRating] = useState('');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [kneadingTime, setKneadingTime] = useState('');
  const [firstRiseTime, setFirstRiseTime] = useState('');
  const [donutSize, setDonutSize] = useState('');
  const [oilTemp, setOilTemp] = useState('');
  const [doughRecipe, setDoughRecipe] = useState('');
  const [glazeRecipe, setGlazeRecipe] = useState('');
  const [secondRiseTime, setSecondRiseTime] = useState('');
  const [waitAfterFridge, setWaitAfterFridge] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { id } = useParams(); // used in edit mode
  const navigate = useNavigate();

  const formData = {
    title,
    rating: rating === '' ? null : parseInt(rating),
    date,
    notes,
    kneading_time: kneadingTime === '' ? null : parseInt(kneadingTime),
    first_rise_time: firstRiseTime === '' ? null : parseInt(firstRiseTime),
    second_rise_time: secondRiseTime === '' ? null : parseInt(secondRiseTime),
    wait_after_fridge: waitAfterFridge === '' ? null : parseInt(waitAfterFridge),
    donut_size: donutSize,
    oil_temp: oilTemp === '' ? null : parseInt(oilTemp),
    dough_recipe: doughRecipe,
    glaze_recipe: glazeRecipe,
  };

  // 🧠 Fetch data if editing
  useEffect(() => {
    if (editMode && id) {
      const fetchLog = async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_EXPRESS_URL}/api/fry-log/${id}`, {
            credentials: 'include',
          });

          if (!res.ok) throw new Error('Failed to fetch log');

          const data = await res.json();
          setTitle(data.title);
          setRating(data.rating);
          setDate(data.date);
          setNotes(data.notes || '');
          setKneadingTime(data.kneading_time || '');
          setFirstRiseTime(data.first_rise_time || '');
          setDonutSize(data.donut_size || '');
          setSecondRiseTime(data.second_rise_time || '');
          setOilTemp(data.oil_temp || '');
          setWaitAfterFridge(data.wait_after_fridge || '');
          setDoughRecipe(data.dough_recipe || '');
          setGlazeRecipe(data.glaze_recipe || '');
        } catch (err) {
          setMessage('Failed to load log.');
        }
      };

      fetchLog();
    }
  }, [editMode, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const method = editMode ? 'PUT' : 'POST';
    const url = `${process.env.REACT_APP_EXPRESS_URL}/api/fry-log${editMode ? `/${id}` : ''}`;

    try {
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formData }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Unknown error');
      }

      navigate('/admin/dashboard');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg mt-10">
      <button
        onClick={() => navigate('/admin/dashboard')}
        className="mb-4 text-pink-600 hover:text-pink-800 font-medium"
      >
        ← Back to Dashboard
      </button>
      <h2 className="text-2xl font-bold text-pink-700 dark:text-pink-300 mb-6 text-center">
        {editMode ? 'Edit Fry Session' : 'Log a Fry Session'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Title
          </label>
          <input
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Rating
          </label>
          <input
            type="number"
            min="1"
            max="10"
            required
            value={rating}
            onChange={(e) => setRating(e.target.value)}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date</label>
          <input
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Kneading Time (minutes)
          </label>
          <input
            type="number"
            value={kneadingTime}
            onChange={(e) => setKneadingTime(e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            First Rise Time (minutes)
          </label>
          <input
            type="number"
            value={firstRiseTime}
            onChange={(e) => setFirstRiseTime(e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Donut Size
          </label>
          <input
            type="text"
            value={donutSize}
            onChange={(e) => setDonutSize(e.target.value)}
            placeholder="e.g. 3 inch"
            className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Second Rise Time (minutes)
          </label>
          <input
            type="number"
            value={secondRiseTime}
            onChange={(e) => setSecondRiseTime(e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Oil Temperature (F)
          </label>
          <input
            type="number"
            value={oilTemp}
            onChange={(e) => setOilTemp(e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Wait Time After Fridge (minutes)
          </label>
          <input
            type="number"
            value={waitAfterFridge}
            onChange={(e) => setWaitAfterFridge(e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Dough Recipe
          </label>
          <textarea
            value={doughRecipe}
            onChange={(e) => setDoughRecipe(e.target.value)}
            rows="3"
            className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Glaze Recipe
          </label>
          <textarea
            value={glazeRecipe}
            onChange={(e) => setGlazeRecipe(e.target.value)}
            rows="3"
            className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="4"
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-black dark:text-white"
            placeholder="Optional notes about the dough, glaze, oil, etc."
          />
        </div>

        {message && <p className="text-red-500 text-sm">{message}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-pink-600 hover:bg-pink-700 text-white py-2 rounded font-semibold transition"
        >
          {loading
            ? editMode
              ? 'Saving...'
              : 'Logging...'
            : editMode
            ? 'Save Changes'
            : 'Log Fry Session'}
        </button>
      </form>
    </div>
  );
}
